import { useEffect, useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { listClients, updateClientSettings, uploadClientLogo } from "@/lib/api";
import { Image as ImageIcon, Upload, Palette } from "lucide-react";

const DEFAULT_BRAND_COLOR = "#0f172a";
const HEX_RE = /^#([0-9a-fA-F]{6})$/;

/**
 * Administration > Branding.
 * Each tenant (client) can set a logo and a primary brand colour.
 * Super-admins see all clients; client-admins see only their own.
 */
export default function AdminBranding() {
  const { hasRole, assignments } = useAuth();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);

  const myClientIds = useMemo(
    () =>
      hasRole(["super_admin"])
        ? null
        : new Set(assignments.filter((a) => a.client_id).map((a) => a.client_id)),
    [hasRole, assignments]
  );

  const refresh = async () => {
    setLoading(true);
    const { data, error } = await listClients();
    if (error) toast.error(error.message || "Could not load clients");
    const rows = (data || []).filter(
      (c) => !myClientIds || myClientIds.has(c.id)
    );
    setClients(rows);
    setLoading(false);
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data, error } = await listClients();
      if (cancelled) return;
      if (error) toast.error(error.message || "Could not load clients");
      const rows = (data || []).filter(
        (c) => !myClientIds || myClientIds.has(c.id)
      );
      setClients(rows);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [myClientIds]);

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-4" data-testid="branding-page">
      <div>
        <h1 className="text-2xl font-semibold">Branding</h1>
        <p className="text-sm text-slate-500">
          Logo and primary colour shown in the app header for each tenant.
        </p>
      </div>

      {loading && <div className="text-sm text-slate-500">Loading clients…</div>}

      {!loading && clients.length === 0 && (
        <Alert>
          <AlertDescription>No clients available to manage.</AlertDescription>
        </Alert>
      )}

      <div className="space-y-4">
        {clients.map((c) => (
          <BrandingCard key={c.id} client={c} onSaved={refresh} />
        ))}
      </div>
    </div>
  );
}

function BrandingCard({ client, onSaved }) {
  const [logoUrl, setLogoUrl] = useState(client.brand_logo_url || "");
  const [logoPath, setLogoPath] = useState(client.brand_logo_path || "");
  const [color, setColor] = useState(client.brand_primary_color || "");
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  const hexValid = !color || HEX_RE.test(color);

  const handleLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setErr(null);
    const { data, error } = await uploadClientLogo(client, file);
    setUploading(false);
    if (error) {
      setErr(error.message || "Upload failed");
      return;
    }
    setLogoUrl(data.url);
    setLogoPath(data.path);
    toast.success("Logo uploaded — remember to Save");
  };

  const handleSave = async () => {
    if (!hexValid) {
      setErr("Primary colour must be a 6-digit hex like #0f172a");
      return;
    }
    setSaving(true);
    setErr(null);
    const { error } = await updateClientSettings(client.id, {
      brand_logo_url: logoUrl || null,
      brand_logo_path: logoPath || null,
      brand_primary_color: color || null,
    });
    setSaving(false);
    if (error) {
      setErr(error.message || JSON.stringify(error));
      return;
    }
    toast.success(`${client.name} — branding saved`);
    onSaved?.();
  };

  const previewColor = HEX_RE.test(color) ? color : DEFAULT_BRAND_COLOR;

  return (
    <Card data-testid={`branding-card-${client.slug}`}>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span>{client.name}</span>
          <span className="text-xs font-normal text-slate-500">{client.slug}</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        {err && (
          <Alert variant="destructive">
            <AlertDescription>{err}</AlertDescription>
          </Alert>
        )}

        {/* Logo */}
        <div className="space-y-2">
          <Label className="flex items-center gap-2">
            <ImageIcon className="h-4 w-4" /> Tenant logo
          </Label>
          <div className="flex items-center gap-4">
            <div
              className="h-16 w-16 rounded-md border bg-slate-50 flex items-center justify-center overflow-hidden"
              data-testid={`logo-preview-${client.slug}`}
            >
              {logoUrl ? (
                <img src={logoUrl} alt={`${client.name} logo`} className="max-h-16 max-w-16 object-contain" />
              ) : (
                <span className="text-[10px] text-slate-400">no logo</span>
              )}
            </div>
            <div className="space-y-1">
              <input
                id={`logo-upload-${client.slug}`}
                type="file"
                accept="image/png,image/jpeg,image/svg+xml,image/webp"
                onChange={handleLogo}
                className="hidden"
                data-testid={`logo-upload-input-${client.slug}`}
              />
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={uploading}
                onClick={() => document.getElementById(`logo-upload-${client.slug}`).click()}
                data-testid={`logo-upload-btn-${client.slug}`}
              >
                <Upload className="h-3.5 w-3.5 mr-1.5" />
                {uploading ? "Uploading…" : "Upload logo"}
              </Button>
              {logoUrl && (
                <p className="text-[11px] text-slate-500 truncate max-w-[260px]">
                  {logoPath || logoUrl}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Primary colour */}
        <div className="space-y-2">
          <Label className="flex items-center gap-2">
            <Palette className="h-4 w-4" /> Primary colour
          </Label>
          <div className="flex items-center gap-3">
            <input
              type="color"
              value={HEX_RE.test(color) ? color : DEFAULT_BRAND_COLOR}
              onChange={(e) => setColor(e.target.value)}
              className="h-9 w-12 rounded border bg-white cursor-pointer"
              aria-label="Pick colour"
              data-testid={`color-picker-${client.slug}`}
            />
            <Input
              value={color}
              onChange={(e) => setColor(e.target.value)}
              placeholder="#0f172a (leave blank for default)"
              className="font-mono w-44"
              data-testid={`color-hex-input-${client.slug}`}
            />
            <div
              className="h-9 px-3 rounded text-white text-xs flex items-center"
              style={{ backgroundColor: previewColor }}
            >
              Preview
            </div>
          </div>
          {!hexValid && (
            <p className="text-[11px] text-rose-600">
              Must be a 6-digit hex code (e.g. #0f172a).
            </p>
          )}
        </div>

        <div className="pt-1 flex justify-end">
          <Button
            onClick={handleSave}
            disabled={saving || uploading || !hexValid}
            data-testid={`branding-save-btn-${client.slug}`}
          >
            {saving ? "Saving…" : "Save branding"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
